import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Grid, Alert, CircularProgress, Chip } from '@mui/material';
import {
  TrendingUp,
  AccountBalance,
  Business,
  Refresh as RefundIcon,
  Star,
  Construction,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { payoutsService } from '@/services/payoutsService';
import toast from 'react-hot-toast';

export function FinancialOverviewTab() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const data = await payoutsService.getPayoutOverview();
      setOverview(data);
    } catch (error: any) {
      console.error('Failed to load payout overview:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load payout overview');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => `₦${amount.toLocaleString()}`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Financial Overview & Analytics
      </Typography>

      {/* Payout Overview Section */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalance fontSize="small" />
        Payout Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary">Pending Payouts</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {overview ? formatCurrency(overview.pendingPayouts.amount) : '₦0'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overview ? `${overview.pendingPayouts.count} requests` : '0 requests'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RefundIcon sx={{ color: 'info.main' }} />
                <Typography variant="body2" color="text.secondary">Active Payouts</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {overview ? formatCurrency(overview.activePayouts.amount) : '₦0'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overview ? `${overview.activePayouts.count} in progress` : '0 in progress'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckIcon sx={{ color: 'success.main' }} />
                <Typography variant="body2" color="text.secondary">Completed Today</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {overview ? formatCurrency(overview.completedToday.amount) : '₦0'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overview ? `${overview.completedToday.count} payouts` : '0 payouts'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp sx={{ color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary">Total Paid Out</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {overview ? formatCurrency(overview.totalPaidOut.amount) : '₦0'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overview ? `${overview.totalPaidOut.count} payouts` : '0 payouts'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Earnings Status Section */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUp fontSize="small" />
        Earnings Status
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckIcon sx={{ color: 'success.main' }} />
                <Typography variant="body2" color="text.secondary">Available to Withdraw</Typography>
              </Box>
              <Typography variant="h4" fontWeight="600" sx={{ color: 'success.main', mb: 1 }}>
                {overview ? formatCurrency(overview.availableEarnings.amount) : '₦0'}
              </Typography>
              <Chip
                label={overview ? `${overview.availableEarnings.drivers} drivers` : '0 drivers'}
                size="small"
                color="success"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LockIcon sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary">Locked (Pending Release)</Typography>
              </Box>
              <Typography variant="h4" fontWeight="600" sx={{ color: 'warning.main', mb: 1 }}>
                {overview ? formatCurrency(overview.lockedEarnings.amount) : '₦0'}
              </Typography>
              <Chip
                label={overview ? `${overview.lockedEarnings.drivers} drivers` : '0 drivers'}
                size="small"
                color="warning"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Other Financial Data - Coming Soon */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Other Financial Data
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ opacity: 0.6 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Business sx={{ color: 'success.main' }} />
                <Typography variant="body2" color="text.secondary">Host Payouts</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">—</Typography>
              <Typography variant="caption" color="text.secondary">Coming soon</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ opacity: 0.6 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RefundIcon sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary">Refunds</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">—</Typography>
              <Typography variant="caption" color="text.secondary">Coming soon</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ opacity: 0.6 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Star sx={{ color: 'info.main' }} />
                <Typography variant="body2" color="text.secondary">Tips</Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">—</Typography>
              <Typography variant="caption" color="text.secondary">Coming soon</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Under Construction Notice */}
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Construction sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Financial Dashboard Coming Soon
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            This overview will provide comprehensive financial analytics including revenue trends, payout summaries,
            refund rates, and platform commission tracking.
          </Typography>

          <Alert severity="info" sx={{ maxWidth: 700, mx: 'auto', textAlign: 'left' }}>
            <Typography variant="body2" gutterBottom>
              <strong>Planned Features:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>Real-time financial metrics and KPIs</li>
              <li>Revenue breakdown by service type (self-drive, with driver, etc.)</li>
              <li>Payout trends and pending approval amounts</li>
              <li>Commission earnings and platform revenue</li>
              <li>Refund analytics and cancellation impact</li>
              <li>Exportable financial reports (CSV, PDF)</li>
              <li>Date range filtering and comparison views</li>
            </Typography>
          </Alert>

          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Key Metrics to be Tracked:
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2, maxWidth: 800, mx: 'auto' }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <TrendingUp sx={{ color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" fontWeight="600">Total Revenue</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Booking fees + service charges + extras
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <AccountBalance sx={{ color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" fontWeight="600">Payout Volume</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total paid to drivers and hosts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <TrendingUp sx={{ color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" fontWeight="600">Net Platform Revenue</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Revenue minus payouts and refunds
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
